import { resolvePreferredEnergyModel } from "@/energyPlotView/utils";
import { Button, ButtonBar } from "@/overlay/components/CommonParts/buttons";
import {
  type AvailableValueNode,
  useAvailableEnsMembers,
} from "@/overlay/components/LayerStackWindow/EnergyPlotSettings/useAvailableEnergyForecastDataTree";
import { FormField } from "@/overlay/components/form-field";
import { Modal } from "@/overlay/components/window/Modal";
import { t } from "@lingui/core/macro";
import { MmDropdown, MmEnsdropdown } from "@mm/zeus-react";
import { useCallback, useEffect, useMemo, useState } from "react";

function arraysEqual(a: string[], b: string[]) {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

interface AddEnergyGroupModalProps {
  forecastType: string;
  availableModelNodes: AvailableValueNode[];
  isOpen: boolean;
  selectedModel: string;
  onSelectModel: (model: string) => void;
  onClose: () => void;
  onConfirm: (ensMembers?: string[]) => void;
}

export function AddEnergyGroupModal({
  forecastType,
  availableModelNodes,
  isOpen,
  selectedModel,
  onSelectModel,
  onClose,
  onConfirm,
}: AddEnergyGroupModalProps) {
  const [selectedEnsMembers, setSelectedEnsMembers] = useState<string[]>([]);
  const { ensMembers, ensQuantiles } = useAvailableEnsMembers(forecastType, selectedModel);
  const availableModelIds = useMemo(
    () => availableModelNodes.map((node) => node.value).filter((value): value is string => !!value),
    [availableModelNodes],
  );

  const modelItems = useMemo(
    () =>
      availableModelNodes.map((node) => ({
        id: node.value || "",
        title: node.value || "",
      })),
    [availableModelNodes],
  );

  useEffect(() => {
    if (!isOpen || availableModelIds.length === 0) {
      return;
    }

    const resolvedModel = resolvePreferredEnergyModel(availableModelIds, selectedModel);
    if (resolvedModel !== selectedModel) {
      onSelectModel(resolvedModel);
    }
  }, [isOpen, availableModelIds, selectedModel, onSelectModel]);

  useEffect(() => {
    if (!isOpen || ensMembers.length === 0) {
      setSelectedEnsMembers((previous) => (previous.length > 0 ? [] : previous));
      return;
    }

    setSelectedEnsMembers((previous) => {
      const validPrevious = previous.filter((member) => [...ensMembers, ...ensQuantiles].includes(member));
      if (validPrevious.length > 0) {
        return arraysEqual(previous, validPrevious) ? previous : validPrevious;
      }

      if (ensMembers.includes("mean")) {
        return arraysEqual(previous, ["mean"]) ? previous : ["mean"];
      }

      const fallback = ensMembers[0] ? [ensMembers[0]] : [];
      return arraysEqual(previous, fallback) ? previous : fallback;
    });
  }, [isOpen, ensMembers, ensQuantiles]);

  const handleSelectionChanged = useCallback(
    (event: { detail: { members: { id: string }[]; quantiles: { id: string }[] } }) => {
      const { members, quantiles } = event.detail;
      const filteredMembers = members.filter((member) => !ensQuantiles.includes(member.id));

      if (filteredMembers.length === 0 && quantiles.length === 0) {
        setSelectedEnsMembers(["mean"]);
      } else {
        setSelectedEnsMembers([...filteredMembers.map((member) => member.id), ...quantiles.map((q) => q.id)]);
      }
    },
    [ensQuantiles],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div style={{ minWidth: "320px" }}>
        <h3>{t`Add Source`}</h3>
        <FormField label={t`Source`}>
          <MmDropdown
            key={selectedModel}
            items={modelItems}
            activeId={selectedModel ? [selectedModel] : []}
            itemSelectedHandler={(selectedItems) => onSelectModel(selectedItems[0]?.id ?? "")}
            placeholder="Select a model"
          />
        </FormField>
        {ensMembers.length > 0 && (
          <FormField label={t`Ens Members`} noSemanticLinking alignTop>
            <MmEnsdropdown
              placeholder={t`Select...`}
              members={ensMembers
                .filter((member) => !ensQuantiles.includes(member))
                .map((member) => ({ id: member, title: member }))}
              quantiles={ensQuantiles.map((quantile) => ({ id: quantile, title: quantile }))}
              selected={selectedEnsMembers}
              onSelectionChanged={handleSelectionChanged}
            />
          </FormField>
        )}
        <ButtonBar align="right" style={{ marginTop: "16px" }}>
          <Button onClick={onClose}>{t`Cancel`}</Button>
          <Button
            primary
            disabled={!selectedModel}
            onClick={() => onConfirm(selectedEnsMembers.length > 0 ? selectedEnsMembers : undefined)}
          >
            {t`Confirm`}
          </Button>
        </ButtonBar>
      </div>
    </Modal>
  );
}
